import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
  IconButton,
} from "@mui/material";
import {
  capitalize,
  getItemBasePrice,
  getOrdinalSuffix,
  getVariantOptionSurchargeForDisplay,
  getVariantTotal,
} from "../../common/util";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import currency from "currency.js";
import ClearIcon from "@mui/icons-material/Clear";

export default function FlavorSelection(props) {
  const { fieldKey, variant, fields } = props;
  const selection = variant.size?.value?.settings?.field?.flavor?.selection || "";

  const [open, setOpen] = useState(false);

  const [flavor, setFlavor] = useState("");
  const [filling, setFilling] = useState("");

  const [selectionRequired, setSelectionRequired] = useState("");
  const [errors, setErrors] = useState([]);

  const getTitle = (selection) => {
    if (selection === "tier") {
      return "Flavors by Tier";
    } else if (selection === "layer") {
      return "Flavors by Layer";
    } else if (selection === "half") {
      return "Half & Half Flavors";
    } else if (selection === "count") {
      return "Select Flavors";
    }
  };

  const validate = () => {
    let arr = [];
    if (selectionRequired.flavor !== Object.values(flavor?.value?.parts || "").length) {
      arr.push("Missing flavor");
    }
    if (
      selectionRequired.filling > 0 &&
      selectionRequired.filling !== Object.values(filling?.value?.parts || "").length
    ) {
      arr.push("Missing filling");
    }
    return arr;
  };

  const getFieldLabel = (index, numSelections) => {
    if (numSelections > 1) {
      if (selection === "half") {
        if (index === 0) {
          return "1st Half";
        } else {
          return "2nd Half";
        }
      } else if (selection === "count") {
        return getOrdinalSuffix(+index + 1);
      } else if (selection === "tier") {
        let str = selection + `-${index + 1}`;
        if (index === 0) {
          str += ` (Bottom)`;
        } else if (index === numSelections - 1) {
          str += ` (Top)`;
        }

        return `${capitalize(`${str}`)}`;
      } else if (selection === "layer") {
        let str = selection + `-${index + 1}`;
        return `${capitalize(`${str}`)}`;
      }
    } else {
      return "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const arr = validate();
    setErrors(arr);

    if (arr.length === 0) {
      props.onChange(flavor, filling);
      setOpen(false);
    }
  };

  const getNameFromParts = (parts, numSelections) => {
    let arr = [];
    if (Object.keys(parts).length > 1) {
      for (let i = 0; i < Object.keys(parts).length; i++) {
        const part = parts[i].name;

        const title = getFieldLabel(i, numSelections);
        arr.push(`${title}: ${part}`);
      }
    } else {
      arr.push(parts[0].name);
    }

    return arr.join(" • ");
  };

  const sortBySurcharge = (o1, o2) => {
    const amt1 = o1.pricing?.amount || o1.pricing?.factor || 0;
    const amt2 = o2.pricing?.amount || o2.pricing?.factor || 0;
    let rslt = +amt1 - +amt2;
    if (rslt === 0) {
      rslt = o1.name.localeCompare(o2.name);
    }

    return rslt;
  };

  const findOptionByName = (name, arr) => {
    const obj = arr.filter((o) => o.name === name)[0] || "";
    const { override, ...opt } = obj;
    return opt;
  };

  const basePrice = props.basePrice || getItemBasePrice(variant);

  let arr = [];
  let iterations = Math.max(selectionRequired.flavor || 0, selectionRequired.filling || 0);
  for (let i = 0; i < iterations; i++) {
    if (i < selectionRequired.flavor) {
      const label = getFieldLabel(i, selectionRequired.flavor) + " Flavor";
      arr.push(
        <FormControl variant="outlined" fullWidth required>
          <InputLabel id="flavor-label">{label}</InputLabel>
          <Select
            value={flavor?.value?.parts?.[i]?.name || ""}
            labelId="flavor-label"
            label={label}
            id="flavor"
            name="flavor"
            onChange={(e) => {
              let tmp = { ...flavor };
              if (!tmp.value) {
                tmp.value = { selection, parts: {} };
              }
              tmp.value.parts[i] = findOptionByName(e.target.value, fields.flavor.field.options);
              tmp.name = getNameFromParts(tmp.value.parts, selectionRequired.flavor);
              tmp.rank = fields[fieldKey].rank || 1;

              tmp.pricing = { amount: getVariantTotal(tmp.value.parts, basePrice) };

              setFlavor(tmp);
            }}
          >
            {fields.flavor.field.options.sort(sortBySurcharge).map((option, idx) => {
              let surcharge = getVariantOptionSurchargeForDisplay(option, basePrice);

              return (
                <MenuItem key={idx} value={option.name}>
                  {option.name} {surcharge && ` (+${surcharge})`}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );
    }
    if (i < selectionRequired.filling) {
      const label = getFieldLabel(i, selectionRequired.filling) + " Filling";

      arr.push(
        <FormControl variant="outlined" fullWidth required>
          <InputLabel id="filling-label">{label}</InputLabel>
          <Select
            value={filling?.value?.parts?.[i]?.name || ""}
            labelId="filling-label"
            label={label}
            id="filling"
            name="filling"
            onChange={(e) => {
              let tmp = { ...filling };
              if (!tmp.value) {
                tmp.value = { selection, parts: {} };
              }
              tmp.value.parts[i] = findOptionByName(e.target.value, fields.filling.field.options);
              tmp.name = getNameFromParts(tmp.value.parts, selectionRequired.filling);
              tmp.rank = fields[fieldKey].rank || 1;
              tmp.pricing = { amount: getVariantTotal(tmp.value.parts, basePrice) };
              setFilling(tmp);
            }}
          >
            {fields.filling.field.options.sort(sortBySurcharge).map((option, idx) => {
              let surcharge = getVariantOptionSurchargeForDisplay(option, basePrice);

              return (
                <MenuItem key={idx} value={option.name}>
                  {option.name} {surcharge && ` (+${surcharge})`}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );
    }
  }

  return (
    <>
      <Link
        underline="none"
        sx={{ cursor: "pointer", color: "inherit" }}
        onClick={() => {
          setFlavor(variant.flavor);
          setFilling(variant.filling);

          if (selection === "tier" && variant.size.value.tier) {
            let o = { flavor: +variant.size.value.tier || 1 };
            if (fields.filling) {
              o.filling = o.flavor;
            }
            setSelectionRequired(o);
          } else if (selection === "layer") {
            let o = { flavor: +variant.size.value.layer || 1 };
            if (fields.filling) {
              if (o.flavor > 1) {
                o.filling = o.flavor - 1;
              } else {
                o.filling = 1;
              }
            }
            setSelectionRequired(o);
          } else if (selection === "half") {
            let o = { flavor: 2 };
            if (fields.filling) {
              o.filling = 2;
            }
            setSelectionRequired(o);
          } else if (selection === "count") {
            setSelectionRequired({
              flavor: +variant.size?.value?.settings?.field?.flavor?.max || 1,
            });
          }

          setErrors([]);
          setOpen(true);
        }}
      >
        <Box sx={{ border: "1px solid #ccc", borderRadius: "4px", padding: "12px 5px 12px 12px" }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              {variant[fieldKey]?.value ? (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {capitalize(fieldKey)} {fields[fieldKey].required && " *"}
                  </Typography>
                  <Typography>
                    {variant[fieldKey].name}{" "}
                    {+variant[fieldKey]?.pricing?.amount > 0
                      ? ` (+${currency(variant[fieldKey].pricing.amount).format()})`
                      : ""}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ color: "#777" }}>
                  {capitalize(fieldKey)}
                  {fields[fieldKey].required && " *"}
                </Typography>
              )}
            </Box>
            <Box>
              {variant[fieldKey]?.value ? (
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onChange("");
                  }}
                >
                  <ClearIcon></ClearIcon>
                </IconButton>
              ) : (
                <ArrowDropDownIcon />
              )}
            </Box>
          </Box>
        </Box>
      </Link>
      <Dialog open={open} fullWidth={true} maxWidth="sm">
        <DialogTitle>{getTitle(selection)}</DialogTitle>
        <DialogContent>
          <Box>
            {arr.map((o, idx) => (
              <Box mt={3}>{o}</Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: "block" }}>
          {errors && errors.length > 0 && (
            <Box my={1}>
              <Alert severity="error">{errors.join(". ")}</Alert>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" color="secondary" fullWidth onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" fullWidth onClick={handleSubmit}>
              SUBMIT
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
